import { Project } from '@gameonly/core';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import {
  CircularProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authenticatedFetch } from '../../authentication/authenticatedFetch';
import useUser, {
  isUserSubscribed,
} from '../../authentication/context/useUser';
import { downloadFile } from '../../project/ProjectsUtils';
import { MenuItemTeasing } from '../../subscription/SubscriptionTeasing';
import { HeaderButton } from './Headers';

export const ExportMenu = ({ 
  project, 
  onOpenSubscription 
}: { 
  project: Project;
  onOpenSubscription?: (title: string, text: string, link: string) => void;
}) => {
  const { user } = useUser();
  const { t } = useTranslation(['common']);
  if (!isUserSubscribed(user)) {
    return (
      <MenuItemTeasing
        teasingButton={t('common:subscription.downloadProject.teasingButton')}
        text={t('common:subscription.downloadProject.modalText')}
        title={t('common:subscription.downloadProject.modalTitle')}
        link={t('common:subscription.downloadProject.knowMoreLink')}
        onOpen={() => onOpenSubscription?.(
          t('common:subscription.downloadProject.modalTitle'),
          t('common:subscription.downloadProject.modalText'),
          t('common:subscription.downloadProject.knowMoreLink')
        )}
      />
    );
  }
  return <DownloadButton project={project} />;
};

const DownloadButton = ({ project }: { project: Project }) => {
  const { t } = useTranslation(['project']);
  const [loading, setLoading] = useState<boolean>(false);
  const handleDownloadProject = async () => {
    setLoading(true);

    const response = await authenticatedFetch(
      `${process.env.REACT_APP_API_URL}/projects/${project._id}`,
      {
        method: 'GET',
        headers: new Headers({
          Accept: 'application/vnd.ms-excel',
          'Content-Type': 'application/json',
        }),
      },
    );
    const statisticFileBlob = await response.blob();

    const filename = `project_${project._id}_${new Date().toISOString()}.xlsx`;

    downloadFile(statisticFileBlob, filename);
    setLoading(false);
  };
  return (
    <MenuItem onClick={handleDownloadProject}>
      <ListItemIcon>
        <DownloadForOfflineOutlinedIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>
        {t('project:download-button.label')}{' '}
        {loading && <CircularProgress sx={{ maxWidth: 20, maxHeight: 20 }} />}
      </ListItemText>
    </MenuItem>
  );
};

const ExportForPartnerButton = ({ onOpen }: { onOpen: () => void }) => {
  const { t } = useTranslation(['project']);
  return (
    <>
      <Tooltip title={t('project:export.tooltip')}>
        <HeaderButton
          onClick={() => {
            onOpen();
          }}
        >
          <p className="h6r">{t('project:export.label')}</p>
        </HeaderButton>
      </Tooltip>
    </>
  );
};
